import { handleRequest } from "../../axios";
import { axiosInstance } from "./JsdhNotifikaceService";
import {
    HYDRANTY_URL,
    HYDRANTY_INFO_URL,
    ADMIN_HYDRANTY_URL,
    ADMIN_HYDRANTY_GPX_URL,
} from "../url";
import type {
    HydrantyGeoJSON,
    HydrantyInfoResponse,
    HydrantyUploadRequest,
    HydrantyGpxUploadRequest,
    HydrantyUploadResponse,
    HydrantyCacheData,
} from "../generated";

// Cache constants
const HYDRANTY_CACHE_KEY = 'hydranty_geojson';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// -----------------------------
// PUBLIC ENDPOINTS
// -----------------------------

const getHydranty = () => {
    return handleRequest<HydrantyGeoJSON>(() =>
        axiosInstance.get(HYDRANTY_URL)
    );
};

const getHydrantyInfo = () => {
    return handleRequest<HydrantyInfoResponse>(() =>
        axiosInstance.get(HYDRANTY_INFO_URL)
    );
};

// -----------------------------
// ADMIN ENDPOINTS
// -----------------------------

const uploadHydranty = (data: HydrantyUploadRequest) => {
    return handleRequest<HydrantyUploadResponse>(() =>
        axiosInstance.post(ADMIN_HYDRANTY_URL, data)
    );
};

const uploadHydrantyGpx = (data: HydrantyGpxUploadRequest) => {
    return handleRequest<HydrantyUploadResponse>(() =>
        axiosInstance.post(ADMIN_HYDRANTY_GPX_URL, data)
    );
};

const deleteHydranty = () => {
    return handleRequest<void>(() =>
        axiosInstance.delete(ADMIN_HYDRANTY_URL)
    );
};

// -----------------------------
// CACHE HELPERS
// -----------------------------

const getCachedHydranty = (): HydrantyGeoJSON | null => {
    try {
        const cached = localStorage.getItem(HYDRANTY_CACHE_KEY);
        if (!cached) return null;

        const parsed: HydrantyCacheData = JSON.parse(cached);
        const now = Date.now();

        if (now - parsed.timestamp > CACHE_TTL_MS) {
            localStorage.removeItem(HYDRANTY_CACHE_KEY);
            return null;
        }

        return parsed.data;
    } catch {
        return null;
    }
};

const setCachedHydranty = (data: HydrantyGeoJSON): void => {
    try {
        const cacheData: HydrantyCacheData = {
            data,
            timestamp: Date.now()
        };
        localStorage.setItem(HYDRANTY_CACHE_KEY, JSON.stringify(cacheData));
    } catch {
        // Failed to cache hydranty data - ignore silently
    }
};

const invalidateHydrantyCache = (): void => {
    localStorage.removeItem(HYDRANTY_CACHE_KEY);
};

const HydrantyService = {
    // Public
    getHydranty,
    getHydrantyInfo,
    // Admin
    uploadHydranty,
    uploadHydrantyGpx,
    deleteHydranty,
    // Cache
    getCachedHydranty,
    setCachedHydranty,
    invalidateHydrantyCache,
    // Constants
    HYDRANTY_CACHE_KEY,
    CACHE_TTL_MS,
};

export default HydrantyService;
